import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { I18nFacade } from './i18n-facade';
import { I18N_FACADE } from './i18n.token';
import { LanguageOption } from './i18n.types';

@Component({
  selector: 'lib-i18n',
  standalone: true,
  imports: [FormsModule, SelectModule, ButtonModule, TranslateModule, ToggleSwitch],
  templateUrl: './i18n.component.html',
  styleUrl: './i18n.component.css',
})
export class I18nComponent implements OnInit, OnDestroy {
  @Input() variant: 'select' | 'toggle' | 'switch' = 'switch';
  @Input() showLabel = false;
  @Input() size: 'small' | 'large' | undefined = undefined;
  @Input() dropdownClass = '';
  @Input() buttonClass = '';
  @Input() containerClass = '';
  @Input() placeholder = 'Language';
  @Input() isEnglish = false;

  languages: LanguageOption[] = [];
  selectedLanguageValue!: string;
  translatedPlaceholder = '';
  private readonly destroy$ = new Subject<void>();
  private readonly i18n = inject<I18nFacade>(I18N_FACADE);

  get selectedLanguage() {
    return this.languages.find((lang) => lang.value === this.selectedLanguageValue);
  }

  ngOnInit(): void {
    this.languages = this.i18n.getAvailableLanguages();
    const currentLang = this.i18n.getCurrentLanguage();
    this.selectedLanguageValue = currentLang;
    this.isEnglish = currentLang === 'en';

    this.translatePlaceholder();

    this.i18n.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe((lang) => {
      this.selectedLanguageValue = lang;
      this.isEnglish = lang === 'en';
      this.translatePlaceholder();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  translatePlaceholder(): void {
    this.translatedPlaceholder = this.i18n.instant(this.placeholder);
  }

  changeLanguage(lang: string): void {
    this.i18n.setLanguage(lang);
    this.selectedLanguageValue = lang;
    this.isEnglish = lang === 'en';
  }

  switchLanguage() {
    this.selectedLanguageValue = this.isEnglish ? 'en' : 'es';
    this.changeLanguage(this.selectedLanguageValue);
  }

  toggleLanguage() {
    this.isEnglish = !this.isEnglish;
    this.selectedLanguageValue = this.isEnglish ? 'en' : 'es';
    this.changeLanguage(this.selectedLanguageValue);
  }

  getCurrentFlag(): string {
    return this.isEnglish ? 'assets/images/usa-flag.png' : 'assets/images/spain-flag.png';
  }

  getCurrentLabel(): string {
    return this.getLabelByValue(this.selectedLanguageValue);
  }

  getLabelByValue(value: string): string {
    return this.languages.find((lang) => lang.value === value)?.label ?? '';
  }
}
